import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import {
  ACTIVATE_CLINICS,
  CHANGE_CLINICS_TIER,
  CHANGE_CLINICS_TYPE,
  CLINICS_EDITING,
  DEACTIVATE_CLINICS,
  IMPORT_CLINICS,
} from "../../../../enums/ApiPaths";
import { clinicActivation } from "../../../../enums/queryFunction/Submit";
import { CLINICS, CLINICS_MASTER } from "../../../../enums/QueryKeys";
import { snackbarSeverity } from "../../../../hooks/useSnackbar";
import authApi from "../../../../utils/api/authApi";

const NON_EDITABLE_CLINIC_FIELDS = [
  "workingDaysList",
  "informationFields",
  "warehouse",
  "tierId",
  "rowNumber",
  "isPending",
];

function getErrorMessage(error) {
  const data = error?.response?.data;
  return typeof data === "string" && data.trim() ? data : "SomethingWentWrong";
}

function toEditableClinic(clinic) {
  const editable = { ...clinic };
  NON_EDITABLE_CLINIC_FIELDS.forEach((field) => delete editable[field]);
  return editable;
}

export default function useClinicListMutations() {
  const queryClient = useQueryClient();
  const { handleSnackbar } = useContext(SnackbarContext);

  function onSuccess() {
    queryClient.invalidateQueries({ queryKey: [CLINICS_MASTER] });
    handleSnackbar("ActionWasSuccessful", snackbarSeverity.SUCCESS);
  }

  function onError(error) {
    handleSnackbar(getErrorMessage(error), snackbarSeverity.ERROR);
  }

  const importClinics = useMutation({
    mutationFn: (clinics) =>
      authApi.post(IMPORT_CLINICS, clinics).then((res) => res.data),
    onSuccess,
    onError,
  });

  const activateClinics = useMutation({
    mutationFn: (clinicIds) => authApi.post(ACTIVATE_CLINICS, clinicIds),
    onSuccess,
    onError,
  });

  const deactivateClinics = useMutation({
    mutationFn: (clinicIds) => authApi.post(DEACTIVATE_CLINICS, clinicIds),
    onSuccess,
    onError,
  });

  const changeTier = useMutation({
    mutationFn: ({ tierId, clinicIds }) =>
      authApi.post(CHANGE_CLINICS_TIER, { tierId, clinicIds }),
    onSuccess: (_, { clinicIds }) => {
      clinicIds.forEach((id) =>
        queryClient.invalidateQueries({ queryKey: [CLINICS + id] }),
      );
      onSuccess();
    },
    onError,
  });

  const changeType = useMutation({
    mutationFn: ({ clinicType, clinicIds }) =>
      authApi.post(CHANGE_CLINICS_TYPE, { clinicType, clinicIds }),
    onSuccess: (_, { clinicIds }) => {
      clinicIds.forEach((id) =>
        queryClient.invalidateQueries({ queryKey: [CLINICS + id] }),
      );
      onSuccess();
    },
    onError,
  });

  const toggleActivation = useMutation({
    mutationFn: (clinic) => clinicActivation(clinic),
    onSuccess: (_, clinic) => {
      queryClient.invalidateQueries({ queryKey: [CLINICS + clinic.id] });
      onSuccess();
    },
    onError,
  });

  const updateClinic = useMutation({
    mutationFn: (clinic) =>
      authApi.post(CLINICS_EDITING, toEditableClinic(clinic)),
    onSuccess: (_, clinic) => {
      queryClient.invalidateQueries({ queryKey: [CLINICS + clinic.id] });
      onSuccess();
    },
    onError,
  });

  return {
    importClinics,
    activateClinics,
    deactivateClinics,
    changeTier,
    changeType,
    toggleActivation,
    updateClinic,
  };
}
